/**
 * Bet evaluators — given a race and a selection, did the bet "cash"?
 *
 * A selection is either a tip object ({ selectionId }) or a raw finishing
 * position (number). The unit these compute over is a "leg": one selection's
 * outcome in one race. See docs/LEXI-PERFORMANCE-REPORTS-SPEC.md §2/§4.
 *
 * Win (1st), Place/Multi (within the paying range) and Each-Way (placed) are
 * all evaluated here. Each-way's blended win+place *return* lives in the ROI
 * maths (metrics.js); the exact staking split is confirmable with Aaron.
 */
import { placesPaid } from "./ladders";

/** Finishing position of a selectionId in a race, or null if not found / DNF. */
export function positionOf(race, selectionId) {
  const runner = (race?.results || []).find(
    (r) => r.selectionId === selectionId,
  );
  return runner && runner.position != null ? runner.position : null;
}

/** Field size to evaluate the ladder against — prefer field at market close. */
export function fieldOf(race) {
  return (
    race?.fieldSizeAtClose ??
    race?.fieldSizeFinal ??
    (race?.results ? race.results.length : 0)
  );
}

/**
 * How many finishing positions "cash" for a bet type:
 *   win → 1st only · place / multi / each-way → the field-size (or lens) ladder.
 * (Each-way's place component pays down the ladder; the win bonus is handled in
 * the ROI maths, not the binary hit test.)
 */
export function payingPlaces(betType, race, opts = {}) {
  if (betType === "win") return 1;
  return placesPaid(fieldOf(race), opts);
}

/**
 * @param {"multi"|"place"|"win"|"eachway"} betType
 * @param {object} race
 * @param {object|number} selection - tip object ({selectionId}) or a raw position.
 * @param {{lens?: string, ladder?: string}} [opts]
 * @returns {boolean} did the leg cash — win: 1st; place/multi: in the paid
 *   range; each-way: placed (the place component returns something).
 */
export function evaluate(betType, race, selection, opts = {}) {
  const pos =
    typeof selection === "number"
      ? selection
      : positionOf(race, selection?.selectionId);

  if (pos == null) return false;

  return pos <= payingPlaces(betType, race, opts);
}
